import React, { useContext, useEffect, useState, useCallback } from 'react';
import type { FC } from 'react';
import { parseISO, isAfter, isValid } from 'date-fns';

import { usePostOfficeExernalMessageProxy } from '@atlassian/post-office-context';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { fg } from '@confluence/feature-gating';
import { useCoordinationClient } from '@confluence/engagement-provider';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { AdminKeyEligibilityQuery } from '@confluence/super-admin/entry-points/AdminKeyEligibilityQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { PremiumTrialDigestDataModal } from '@confluence/premium-trial';
import { RtNeutralMessagingSuppressComponentsHocLoadable } from '@confluence/experiment-reverse-trial-message-suppression/entry-points/ui/RtNeutralMessagingSuppressComponentsHoc.tsx';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useExperimentalSiteUserPersonalization } from '@confluence/targeting/entry-points/useExperimentalSiteUserPersonalization.ts';

import { PremiumAdminOnboarding } from './PremiumAdminOnboarding.tsx';
import { PremiumUserOnboarding } from './PremiumUserOnboarding.tsx';
import { PremiumTrialOnboardingModal, TriggerFrom } from './PremiumTrialOnboardingModal.tsx';
import { useIsPremiumOnboardingFeatureDiscoveryEnabled } from './useIsPremiumOnboardingFeatureDiscoveryEnabled.tsx';
import {
	getIsPremiumOnboardingHappened,
	setIsPremiumOnboardingHappened,
} from './isOnboardingHappenedUtils.ts';
import { useShowMidTrialPremiumOnboardingModal } from './useShowMidTrialPremiumOnboardingModal.ts';
import { PremiumTrialOnboardingReverseTrialModal } from './PremiumTrialOnboardingReverseTrialModal.tsx';
import { usePTOModalVisibilityOnReverseTrial } from './usePTOModalVisibilityOnReverseTrial.tsx';
import { getIsTrialInLast15Days } from './getIsTrialInLast15Days.ts';
import { useIsConfluenceAutoprovisionEditionParity } from './useIsConfluenceAutoprovisionEditionParity.ts';

const PREMIUM_ONBOARDING_MESSAGE_ID = 'confluence-premium-onboarding';
const MID_TRIAL_PREMIUM_ONBOARDING_MESSAGE_ID = 'upflow-confluence-persistent-upgrade';
const CONFLUENCE_FIRST_ACTIVE_TRAIT = 'confluence_first_active';

// Cutover based on a deprecated fg and NOT the official PO cutover date
export const POST_OFFICE_CUTOVER_DATE = parseISO('2025-12-09T23:00:00.000Z'); // Dec 9, 2025 3PST / 6EST

type CoordinationWrapperProps = {
	messageId: string;
	children: React.FC<{ stop: () => void }>;
};

const CoordinationWrapper = ({ messageId, children }: CoordinationWrapperProps) => {
	const { cloudId, userId } = useSessionData();
	const isLicensed = useIsLicensed();

	const coordinationClient = useCoordinationClient();
	const [isActiveThroughEP, setIsActiveThroughEp] = useState(false);

	const postOfficeProxy = usePostOfficeExernalMessageProxy(messageId);

	const initWithPostOffice = useCallback(() => {
		postOfficeProxy.requestMessageStart();
	}, [postOfficeProxy]);

	// Former EP message without a trait requires special handling in PO post-migration
	const isPremiumOnboardingMessage = messageId === PREMIUM_ONBOARDING_MESSAGE_ID;

	// MID_TRIAL_PREMIUM_ONBOARDING_MESSAGE_ID was migrated under the ltops flag
	const shouldUsePostOffice = isPremiumOnboardingMessage
		? fg('cc-engagement-migrate-connie-premium-onboarding')
		: fg('ltops-227-migrate-confluence-upflow-to-post-office');

	const isLicensedUser = isLicensed && userId;
	const shouldCheckNewUserEligibility = isPremiumOnboardingMessage && shouldUsePostOffice;

	/**
	 * Most modals that share PREMIUM_ONBOARDING_MESSAGE_ID have specific targeting criteria to render
	 * However, the generic PremiumAdminOnboarding and PremiumUserOnboarding modals being returned at the end do not
	 *
	 * The original EP message was not managed through a trait resulting in no transferable record of users who viewed the nudge
	 * This means users who have viewed it before will be eligible to see it again in PO
	 * To prevent this, the simplest solution is to ensure only new users after the PO cutover are eligible
	 *
	 * For future messages in this file, a new PO message should be created
	 */
	const { data: userPersonalizationData, loading: userPersonalizationDataLoading } =
		useExperimentalSiteUserPersonalization({
			variables: { cloudId, userId: userId as string },
			skip: !isLicensedUser || !shouldCheckNewUserEligibility,
		});

	const attributes = userPersonalizationData?.experimentalSiteUserPersonalization?.attributes;
	const confluenceFirstActive = attributes?.find(
		({ name }: { name: string }) => name === CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;

	const firstActiveDate = confluenceFirstActive ? new Date(confluenceFirstActive) : null;
	const isNewUserAfterPOCutover =
		firstActiveDate && isValid(firstActiveDate)
			? isAfter(firstActiveDate, POST_OFFICE_CUTOVER_DATE)
			: false;

	const initWithEngagementPlatform = useCallback(async () => {
		const result = await coordinationClient.start(messageId);
		setIsActiveThroughEp(result);
	}, [coordinationClient, messageId]);

	const isMessageActive: boolean = shouldUsePostOffice
		? (postOfficeProxy.hasMessageStarted ?? false)
		: isActiveThroughEP;

	useEffect(() => {
		if (!isPremiumOnboardingMessage) {
			shouldUsePostOffice ? void initWithPostOffice() : void initWithEngagementPlatform();
			return;
		}

		if (isNewUserAfterPOCutover && shouldUsePostOffice) {
			void initWithPostOffice();
		}

		// clean up function to allow other coordinated messsages to show
		return () => {
			if (!isMessageActive) {
				return;
			}

			if (shouldUsePostOffice) {
				postOfficeProxy.dismissMessage();
			} else {
				coordinationClient.stop(messageId);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const stop = useCallback(() => {
		if (shouldUsePostOffice) {
			postOfficeProxy.dismissMessage();
		} else {
			coordinationClient.stop(messageId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postOfficeProxy, coordinationClient, messageId]);

	if (!isMessageActive || userPersonalizationDataLoading) return null;

	return children({ stop });
};

export const PremiumOnboarding: FC<{}> = () => {
	const { edition } = useSessionData();
	const { isNewUser, isSiteAdmin, error, loading } = useContext(SPAViewContext);
	const {
		isPremiumOnboardingFeatureDiscoveryEnabled,
		isPTORTEnabled,
		inTrialError: experimentInTrialQueryError,
		reverseTrialError: experimentReverseTrialQueryError,
		loading: experimentPremiumOnboardModalLoading,
		trialDaysLeft,
	} = useIsPremiumOnboardingFeatureDiscoveryEnabled({
		isPremiumOnboardingEnabled: fg('cc_onboarding_experience'),
	});

	const shouldShowPremiumTrialDigestDataModal =
		trialDaysLeft <= 5 && trialDaysLeft > 0 && isSiteAdmin && edition === ConfluenceEdition.PREMIUM;

	const {
		showPTORTModal,
		showMidTrialModal: showPTORTMidTrialModal,
		loading: checkPTOModalVisibilityLoading,
	} = usePTOModalVisibilityOnReverseTrial({
		isPTORTEnabled,
		isSiteAdmin,
	});

	const { shouldShowMidTrialPremiumOnboardingModal, stopMidTrialPremiumOnboardingModal } =
		useShowMidTrialPremiumOnboardingModal({
			isPremiumOnboardingFeatureDiscoveryEnabled,
			isSiteAdmin,
			isTrialHalfWayThrough: isPTORTEnabled
				? showPTORTMidTrialModal
				: getIsTrialInLast15Days(trialDaysLeft),
		});

	const isConfluenceAutoprovisionEditionParity = useIsConfluenceAutoprovisionEditionParity();

	// if confluence is auto provisioned and is in variation for the confluence_autoprovisioning_edition_parity experiment,
	// don't show the premium onboarding modal
	if (isConfluenceAutoprovisionEditionParity) {
		return null;
	}

	if (
		edition !== ConfluenceEdition.PREMIUM ||
		isNewUser ||
		error ||
		loading ||
		experimentPremiumOnboardModalLoading ||
		checkPTOModalVisibilityLoading ||
		!fg('cc_onboarding_experience')
	)
		return null;

	// We dont want to visually display anything when experiment goes wrong.
	if (experimentInTrialQueryError) {
		return <ErrorDisplay error={experimentInTrialQueryError} />;
	}
	if (experimentReverseTrialQueryError) {
		return <ErrorDisplay error={experimentReverseTrialQueryError} />;
	}

	if (isPTORTEnabled && !showPTORTModal && !shouldShowMidTrialPremiumOnboardingModal) {
		return null;
	}

	//render premium trial digest data modal when iPUF eligibility conditions are met
	if (shouldShowPremiumTrialDigestDataModal) {
		return <PremiumTrialDigestDataModal daysLeft={trialDaysLeft} />;
	}

	// PremiumTrialOnboardingReverseTrialModal maybe returned before shouldShowMidTrialPremiumOnboardingModal becomes true.
	// This means that EP message PREMIUM_ONBOARDING_MESSAGE_ID will start before MID_TRIAL_PREMIUM_ONBOARDING_MESSAGE_ID.
	// However, PREMIUM_ONBOARDING_MESSAGE_ID start will fail because it should have already been exposed before mid trial modal shows.
	// Therefore, the mid trial modal is should be safe to start.
	if (shouldShowMidTrialPremiumOnboardingModal) {
		const source =
			isPTORTEnabled && showPTORTMidTrialModal
				? 'premiumTrialOnboardingReverseTrialMidtrialModal'
				: 'premiumTrialOnboardingModal';
		return (
			<RtNeutralMessagingSuppressComponentsHocLoadable
				extraAnalyticsAttributes={{
					componentSource: source,
					triggerFrom: TriggerFrom.MID_TRIAL_ONBOARDING,
				}}
			>
				<CoordinationWrapper messageId={MID_TRIAL_PREMIUM_ONBOARDING_MESSAGE_ID}>
					{({ stop }) => {
						return (
							<PremiumTrialOnboardingModal
								daysLeft={trialDaysLeft}
								triggerFrom={TriggerFrom.MID_TRIAL_ONBOARDING}
								onClose={() => {
									stopMidTrialPremiumOnboardingModal();
									stop();
								}}
								source={source}
							/>
						);
					}}
				</CoordinationWrapper>
			</RtNeutralMessagingSuppressComponentsHocLoadable>
		);
	}

	if (isPremiumOnboardingFeatureDiscoveryEnabled && getIsPremiumOnboardingHappened(true))
		setIsPremiumOnboardingHappened(false);

	if (showPTORTModal) {
		return (
			<RtNeutralMessagingSuppressComponentsHocLoadable
				extraAnalyticsAttributes={{
					componentSource: 'PremiumTrialOnboardingReverseTrialModal',
				}}
			>
				<CoordinationWrapper messageId={PREMIUM_ONBOARDING_MESSAGE_ID}>
					{({ stop }) => {
						return <PremiumTrialOnboardingReverseTrialModal stop={stop} />;
					}}
				</CoordinationWrapper>
			</RtNeutralMessagingSuppressComponentsHocLoadable>
		);
	}

	if (isPremiumOnboardingFeatureDiscoveryEnabled && !isPTORTEnabled) {
		return (
			<CoordinationWrapper messageId={PREMIUM_ONBOARDING_MESSAGE_ID}>
				{({ stop }) => {
					return <PremiumTrialOnboardingModal triggerFrom={TriggerFrom.ONBOARDING} stop={stop} />;
				}}
			</CoordinationWrapper>
		);
	}

	return (
		<AdminKeyEligibilityQuery>
			{({ canBeSuperAdmin }) => {
				return (
					<CoordinationWrapper messageId={PREMIUM_ONBOARDING_MESSAGE_ID}>
						{({ stop }) => {
							return canBeSuperAdmin ? (
								<PremiumAdminOnboarding stop={stop} />
							) : (
								<PremiumUserOnboarding stop={stop} />
							);
						}}
					</CoordinationWrapper>
				);
			}}
		</AdminKeyEligibilityQuery>
	);
};
